import { generateTitle } from 'utils/charts/common';
import {
    LESS_THAN_OR_EQUAL_TO_TEN_PERCENT_ABOVE_UPPER_LIMIT,
    MORE_THAN_TEN_PERCENT_ABOVE_UPPER_LIMIT,
    MORE_THAN_TWENTY_PERCENT_ABOVE_UPPER_LIMIT,
    OUTLIER_DETECTION_TITLE,
    OUTLIER_PART_TWO_BACK_ARROW_URL,
    PERCENT_OF_EXECUTION_ONLY_TRADES,
    RED_DIAMOND_GRAPHIC_URL,
    RED_DIAMOND_OP20_URL,
    RED_DIAMOND_OP50_URL,
    RED_DIAMOND_URL,
    RED_WHITE_CIRCLE_10_URL,
    RED_WHITE_CIRCLE_20_URL,
    RED_WHITE_CIRCLE_5_URL,
    UPPER_LIMIT_TEN_PERCENT,
    UPPER_LIMIT_TWENTY_PERCENT
} from 'utils/charts/constants';

const moreThanTenPercentAboveLimitGraphic = {
    image: {
        type: 'image',
        style: {
            image: RED_WHITE_CIRCLE_5_URL,
            width: 12,
            height: 12,
            opacity: 0.4
        },
        cursor: 'default',
        bottom: 10
    },
    text: {
        type: 'text',
        style: {
            text: LESS_THAN_OR_EQUAL_TO_TEN_PERCENT_ABOVE_UPPER_LIMIT,
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '3%'
    }
};

const tenPercentAboveLimitGraphic = {
    image: {
        type: 'image',
        style: {
            image: RED_WHITE_CIRCLE_10_URL,
            width: 12,
            height: 12
        },
        cursor: 'default',
        bottom: 10,
        left: '37%'
    },
    text: {
        type: 'text',
        style: {
            text: MORE_THAN_TEN_PERCENT_ABOVE_UPPER_LIMIT,
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '40%'
    }
};

const twentyPercentAboveLimitGraphic = {
    image: {
        type: 'image',
        style: {
            image: RED_WHITE_CIRCLE_20_URL,
            width: 12,
            height: 12
        },
        cursor: 'default',
        bottom: 10,
        left: '74%'
    },
    text: {
        type: 'text',
        style: {
            text: MORE_THAN_TWENTY_PERCENT_ABOVE_UPPER_LIMIT,
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '77%'
    }
};

const rmSymbolAndTextGraphic = {
    image: {
        type: 'image',
        style: {
            image: RED_DIAMOND_GRAPHIC_URL,
            width: 12,
            height: 12
        },
        cursor: 'default',
        top: 5,
        right: 45
    },
    text: {
        type: 'text',
        style: {
            text: 'RM',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        top: 5,
        right: 20
    }
};

const partOneTooltip = (outliers) => {
    return {
        formatter: function (params) {
            const { name } = params;
            const hoveredOutlier = outliers
                ? outliers.find((o) => o.rmEmployeeId === name)
                : [];
            const {
                rmRegion,
                rmGroup,
                rmEmployeeId,
                percentEo,
                totalEoTrades,
                rank,
                score
            } = { ...hoveredOutlier };
            return (
                '<span style="line-height: 30px;">CTO Code</span>' +
                '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                rmEmployeeId +
                '</span>' +
                '<br><span style="line-height: 30px;">Team</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                rmGroup +
                '</span>' +
                '<br><span style="line-height: 30px;">Market</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                rmRegion +
                '</span>' +
                '<br><span style="line-height: 30px;">% of EO Trades</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                percentEo +
                '%' +
                '</span>' +
                '<br><span style="line-height: 30px;"># of EO Trades</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                totalEoTrades +
                '</span>' +
                '<br><span style="line-height: 30px;">S3 Rank</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                rank +
                '</span>' +
                '<br><span style="line-height: 30px;">S3 Risk Score</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                score +
                '</span>'
            );
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};

const calculateSymbolForOutliers = (value) => {
    if (value <= UPPER_LIMIT_TEN_PERCENT) return RED_DIAMOND_OP20_URL;
    if (value > UPPER_LIMIT_TEN_PERCENT && value <= UPPER_LIMIT_TWENTY_PERCENT)
        return RED_DIAMOND_OP50_URL;
    if (value > UPPER_LIMIT_TWENTY_PERCENT) return RED_DIAMOND_URL;
    return RED_DIAMOND_OP20_URL;
};
export const scenarioThreeOutlierChartPartOneOptions = (data) => {
    const { max, outlierMin, outliers } = { ...data };
    return {
        title: generateTitle(OUTLIER_DETECTION_TITLE),
        tooltip: partOneTooltip(outliers),
        // Declare an x-axis (category axis).
        // The category map the first column in the dataset by default.
        xAxis: {
            type: 'category',
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: { show: false }
        },
        // Declare a y-axis (value axis).
        yAxis: [
            {
                type: 'value',
                name: PERCENT_OF_EXECUTION_ONLY_TRADES,
                nameLocation: 'center',
                nameGap: 75,
                nameTextStyle: {
                    padding: [20, 0, 0, 0],
                    color: '#8DA8B3',
                    fontWeight: 'bold'
                },
                splitArea: {
                    show: true,
                    areaStyle: {
                        color: '#FFFFFF'
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: ['#FFE6BE', '#FFD4DB'],
                        width: 1.5
                    }
                },
                axisLabel: {
                    show: true,
                    formatter: function (value) {
                        return value + '%';
                    }
                },
                interval: max,
                min: data ? outlierMin : 0,
                max: data ? max : 100
            },
            {
                type: 'value',
                name: 'Min',
                nameTextStyle: {
                    align: 'left',
                    verticalAlign: 'top',
                    padding: [298, 0, 0, 8]
                }
            },
            {
                type: 'value',
                name: 'Max',
                nameTextStyle: {
                    align: 'left',
                    verticalAlign: 'top',
                    padding: [10, 0, 0, 8]
                }
            }
        ],
        // Plot the scatter for part one
        series: [
            {
                type: 'scatter',
                symbolSize: 12,
                data: data
                    ? outliers.map((outlier, index) => {
                          return {
                              value: [index, outlier.percentEo],
                              name: outlier.rmEmployeeId,
                              symbol: calculateSymbolForOutliers(
                                  outlier.percentAbove
                              ),
                              symbolSize: 12,
                              emphasis: {
                                  focus: 'self',
                                  label: {
                                      show: true,
                                      position: 'top',
                                      color: '#244B60',
                                      fontSize: '12px',
                                      fontWeight: 700,
                                      formatter: function (params) {
                                          return params.name;
                                      }
                                  }
                              },
                              blur: {
                                  label: {
                                      show: false
                                  }
                              }
                          };
                      })
                    : []
            }
        ],
        grid: {
            width: '350px',
            left: '95px'
        },
        graphic: [
            moreThanTenPercentAboveLimitGraphic.image,
            moreThanTenPercentAboveLimitGraphic.text,
            tenPercentAboveLimitGraphic.image,
            tenPercentAboveLimitGraphic.text,
            twentyPercentAboveLimitGraphic.image,
            twentyPercentAboveLimitGraphic.text,
            rmSymbolAndTextGraphic.image,
            rmSymbolAndTextGraphic.text
        ]
    };
};

const partTwoTooltip = (outlier) => {
    return {
        formatter: function () {
            const {
                rmRegion,
                rmGroup,
                rmEmployeeId,
                percentEo,
                percentAbove,
                totalEoTrades
            } = outlier;
            return outlier
                ? '<span style="line-height: 30px;">CTO Code</span>' +
                      '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                      rmEmployeeId +
                      '</span>' +
                      '<br><span style="line-height: 30px;">Team</span>' +
                      '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                      rmGroup +
                      '</span>' +
                      '<br><span style="line-height: 30px;">Market</span>' +
                      '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                      rmRegion +
                      '</span>' +
                      '<br><span style="line-height: 30px;">% above Upper Limit</span>' +
                      '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                      percentAbove +
                      '%' +
                      '</span>' +
                      '<br><span style="line-height: 30px;">% of EO Trades</span>' +
                      '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                      percentEo +
                      '%' +
                      '</span>' +
                      '<br><span style="line-height: 30px;"># of EO Trades</span>' +
                      '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                      totalEoTrades +
                      '</span>'
                : null;
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        position: function (point) {
            // fixed at top
            return [point[0], '30%'];
        },
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};
export const scenarioThreeOutlierChartPartTwoOptions = (stats, outlier) => ({
    title: generateTitle(
        `${OUTLIER_DETECTION_TITLE} - ${outlier ? outlier.rmEmployeeId : null}`,
        '30'
    ),
    tooltip: {
        trigger: 'item'
    },
    dataset: [
        {
            // prettier-ignore
            source: stats ? [
                [
                    stats.min,
                    stats.lower,
                    stats.lower,
                    stats.upper,
                    stats.max
                ]
            ] : []
        },
        {
            fromDatasetIndex: 0,
            transform: { type: 'boxplot' }
        }
    ],
    grid: {
        width: '300px',
        left: '20%'
    },
    xAxis: {
        type: 'category',
        boundaryGap: true,
        nameGap: 30,
        splitArea: {
            show: false
        },
        splitLine: {
            show: false
        },
        axisLine: {
            show: false
        },
        axisLabel: {
            show: false
        },
        axisTick: {
            show: false
        },
        min: 0,
        max: 0
    },
    yAxis: [
        {
            type: 'value',
            axisLabel: {
                show: false
            },
            name: PERCENT_OF_EXECUTION_ONLY_TRADES,
            nameLocation: 'center',
            nameGap: 85,
            nameTextStyle: {
                padding: [20, 0, 0, 0],
                color: '#8DA8B3',
                fontWeight: 'bold'
            },
            min: stats ? stats.min : 0,
            max: stats ? stats.max : 100,
            splitLine: {
                lineStyle: {
                    color: [
                        '#C4E7DA',
                        'transparent',
                        'transparent',
                        'transparent',
                        'transparent',
                        '#FFD4DB'
                    ],
                    width: 1.5
                },
                interval: 0
            }
        },
        {
            type: 'value',
            name: `${stats ? stats.max : 100}%`,
            nameTextStyle: {
                align: 'right',
                verticalAlign: 'top',
                padding: [10, 305, 0, 0],
                color: '#929292'
            }
        },
        {
            type: 'value',
            name: `${stats ? stats.min : 0}%`,
            nameTextStyle: {
                align: 'right',
                verticalAlign: 'top',
                padding: [298, 305, 0, 0],
                color: '#929292'
            }
        },
        {
            type: 'value',
            name: 'Max',
            nameTextStyle: {
                align: 'left',
                verticalAlign: 'top',
                padding: [10, 0, 0, 10],
                color: '#222222'
            }
        },
        {
            type: 'value',
            name: 'Min',
            nameTextStyle: {
                align: 'left',
                verticalAlign: 'top',
                padding: [298, 0, 0, 10],
                color: '#222222'
            }
        },
        {
            type: 'value',
            name: `${stats ? stats.upper : 0}%`,
            nameTextStyle: {
                align: 'right',
                verticalAlign: 'top',
                padding: stats
                    ? [315 * (1 - stats.upper / 100), 185, 0, 0]
                    : [0, 0, 0, 0],
                color: '#929292'
            }
        },
        {
            type: 'value',
            name: 'Upper Limit',
            nameTextStyle: {
                align: 'left',
                verticalAlign: 'top',
                padding: stats
                    ? [315 * (1 - stats.upper / 100), 0, 0, -115]
                    : [0, 0, 0, 0],
                color: '#222222'
            }
        },
        {
            type: 'value',
            name: `${stats ? stats.lower : 0}%`,
            nameTextStyle: {
                align: 'right',
                verticalAlign: 'top',
                padding: stats
                    ? [290 * (1 - stats.lower / 100) + 10, 185, 0, 0]
                    : [0, 0, 0, 0],
                color: '#929292'
            }
        },
        {
            type: 'value',
            name: 'Lower Limit',
            nameTextStyle: {
                align: 'left',
                verticalAlign: 'top',
                padding: stats
                    ? [290 * (1 - stats.lower / 100) + 10, 0, 0, -115]
                    : [0, 0, 0, 0],
                color: '#222222'
            }
        }
    ],
    graphic: [
        {
            type: 'image',
            id: 'backArrow',
            style: {
                image: OUTLIER_PART_TWO_BACK_ARROW_URL,
                width: 7,
                height: 9
            },
            cursor: 'pointer',
            top: 6,
            left: 20
        },
        moreThanTenPercentAboveLimitGraphic.image,
        moreThanTenPercentAboveLimitGraphic.text,
        tenPercentAboveLimitGraphic.image,
        tenPercentAboveLimitGraphic.text,
        twentyPercentAboveLimitGraphic.image,
        twentyPercentAboveLimitGraphic.text,
        rmSymbolAndTextGraphic.image,
        rmSymbolAndTextGraphic.text
    ],
    series: [
        {
            name: 'boxplot',
            type: 'boxplot',
            datasetIndex: 1,
            tooltip: {
                show: false
            },
            itemStyle: {
                color: '#FDFBFA',
                borderColor: '#CECECE'
            },
            emphasis: {
                disabled: true
            }
        },
        {
            type: 'scatter',
            symbolSize: 12,
            data: outlier
                ? [
                      {
                          value: [0, outlier.percentEo],
                          name: outlier.rmEmployeeId,
                          symbol: calculateSymbolForOutliers(
                              outlier.percentAbove
                          ),
                          label: {
                              show: true,
                              formatter: function () {
                                  return outlier.rmEmployeeId;
                              },
                              color: '#244B60',
                              fontWeight: 700,
                              offset: [25, 0]
                          },
                          tooltip: partTwoTooltip(outlier),
                          zlevel: 99
                      }
                  ]
                : []
        }
    ]
});
